import { Suspense } from 'react';
import { useRouteContext } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import type { LegalContentType } from '@/content/legal-content';
import { Header } from './header';
import { Footer } from './footer';
import { Layout } from './layout';
import { PageSeo } from './page-seo';

/**
 * Typography for the legal markdown, as Tailwind arbitrary variants rather than the `unreset-tailwind`
 * class `www` and the portals use on the SAME html.
 *
 * `unreset-tailwind` is `all: revert` on every descendant, which hands the browser back its defaults —
 * Times New Roman at 16px. That is survivable on `www`, whose pages are plain either way, but here the
 * article sits between the redesign's header and footer, so reverting would drop the brand face and ink
 * palette on the one block of the page carrying the text. Styling the elements instead keeps the
 * content inheriting `font-brand text-ink` from Layout. `[&_p]` is the idiom the redesign already uses
 * to reach markup it does not render itself — see CONTACT_FORM_METRICS in contact/panel.tsx.
 */
const LEGAL_PROSE = [
	'mx-auto max-w-3xl px-6 py-16 md:py-24',
	'[&_h1]:mb-8 [&_h1]:text-4xl [&_h1]:font-semibold [&_h1]:tracking-tight md:[&_h1]:text-5xl',
	'[&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-2xl [&_h2]:font-medium',
	'[&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-xl [&_h3]:font-medium',
	'[&_p]:text-ink/80 [&_p]:mb-4 [&_p]:leading-[1.6]',
	'[&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6',
	'[&_li]:text-ink/80 [&_li]:mb-2 [&_li]:leading-[1.6]',
	'[&_a]:text-brand-strong [&_a]:break-words [&_a]:underline [&_a]:underline-offset-2',
	'[&_strong]:text-ink [&_strong]:font-medium',
	'[&_table]:my-6 [&_table]:w-full [&_table]:text-left [&_th]:border-b [&_th]:p-2 [&_td]:border-b [&_td]:p-2',
].join(' ');

/**
 * The markdown, compiled to HTML at build time and served by `core.getLegalContent`. Its own component
 * so the suspense it raises is caught around the article alone: the header, the footer and the head are
 * static and have no reason to wait for it.
 *
 * `dangerouslySetInnerHTML` over content that ships IN the bundle (`src/content/**.md`), never over
 * anything a visitor supplies.
 */
function LegalArticle({ type }: { type: LegalContentType }) {
	const { locale } = useRouteContext({ from: '__root__' });
	const { data: html } = useSuspenseQuery(trpc.core.getLegalContent.queryOptions({ type, locale }));
	return <article className={LEGAL_PROSE} dangerouslySetInnerHTML={{ __html: html }} />;
}

/**
 * The four legal pages of the redesigned site, which differ only in which document they show.
 *
 * They exist as their OWN routes rather than as links to `www`'s copies because the router picks its
 * slug context from the HOST (`slugContextFor` in router.tsx): a `/www/...` link rendered on
 * the redesign host came back as `/aviso-legal`, a slug `wwwSlugs` does not know, and landed on the
 * group's 404. That was TEC-3821 B7 — the links were there, the pages were not.
 *
 * The CONTENT is shared with `www` and the portals: one markdown file per document per locale, so the
 * legal text cannot say two different things on two hosts. `getLegalContentHtml` falls back to English
 * for a locale with no file of its own, which today is French.
 */
export function LegalPage(props: {
	type: LegalContentType;
	/** The page's `slugs` export, from its co-located `.meta.ts`. */
	slugs: Record<string, string>;
	title: string;
	description: string;
}) {
	return (
		<Layout>
			<Header />
			<PageSeo slugs={props.slugs} title={props.title} description={props.description} />
			<Suspense fallback={<div className="min-h-[60vh]" />}>
				<LegalArticle type={props.type} />
			</Suspense>
			<Footer />
		</Layout>
	);
}
